import { useCallback, useRef } from 'react';

import { type CookieSingleInstrumentationConfig } from '../context/AIEventsInstrumentationContext';
import { getNewSingleInstrumentationID } from '../effects/getNewSingleInstrumentationID';
import {
	readSingleInstrumentationIDCookie,
	setSingleInstrumentationIDCookie,
} from '../effects/singleInstrumentationIDCookieManagement';
import { AISessionState } from '../events';

export const useAIInstrumentationSetup = (
	customSingleInstrumentationIDFromUserConfig: string | undefined,
	cookieSingleInstrumentationConfig?: CookieSingleInstrumentationConfig,
): { getAISessionState: () => AISessionState; getSingleInstrumentationID: () => string | undefined; refreshSingleInstrumentationID: (customSingleInstrumentationIDFromEvent?: string) => void; setAISessionState: (nextState: AISessionState) => void; } => {
	const singleInstrumentationIDRef = useRef<string | undefined>(undefined);
	const aiSessionStateRef = useRef(AISessionState.unset);

	const setAISessionState = useCallback((nextState: AISessionState): void => {
		aiSessionStateRef.current = nextState;
	}, []);

	const getAISessionState = useCallback((): AISessionState => {
		return aiSessionStateRef.current;
	}, []);

	const refreshSingleInstrumentationID = useCallback(
		(customSingleInstrumentationIDFromEvent?: string): void => {
			const nextSingleInstrumentationID = getNewSingleInstrumentationID(
				customSingleInstrumentationIDFromEvent,
				customSingleInstrumentationIDFromUserConfig,
			);

			singleInstrumentationIDRef.current = nextSingleInstrumentationID;

			if (cookieSingleInstrumentationConfig) {
				void setSingleInstrumentationIDCookie(
					cookieSingleInstrumentationConfig.uniqueCookieFeatureName,
					nextSingleInstrumentationID,
				);
			}
		},
		[customSingleInstrumentationIDFromUserConfig, cookieSingleInstrumentationConfig],
	);

	const getSingleInstrumentationID = useCallback((): string | undefined => {
		// this handles a race condition when we have a new singleInstrumentationID from the user config
		// but Provider knows only about the initial singleInstrumentationID. In this case we need to update
		// singleInstrumentationID at first step and then return a new one.
		if (
			customSingleInstrumentationIDFromUserConfig &&
			customSingleInstrumentationIDFromUserConfig !== singleInstrumentationIDRef.current
		) {
			refreshSingleInstrumentationID();
			return singleInstrumentationIDRef.current;
		}

		if (cookieSingleInstrumentationConfig) {
			return readSingleInstrumentationIDCookie(
				cookieSingleInstrumentationConfig.uniqueCookieFeatureName,
			);
		}

		return singleInstrumentationIDRef.current;
	}, [
		customSingleInstrumentationIDFromUserConfig,
		cookieSingleInstrumentationConfig,
		refreshSingleInstrumentationID,
	]);

	return {
		refreshSingleInstrumentationID,
		getSingleInstrumentationID,
		setAISessionState,
		getAISessionState,
	};
};
